import { useGetUserProfile } from "@leagueplatform/web-common";
import { AppInitDetails, SegmentationInfo } from "api/get-app-init-details";
import { useAppInitDetails } from "hooks/use-app-init-details/use-app-init-details.hook";

export type HraInfo = {
  campaignId?: string;
  activityId: string;
};

// Resolves the HraInfo by trying, in order: selectedKID, first active
// eligibilityOption, first eligibilityOption. Returns null if no activityId
// is found at any step.
const resolveHraInfo = (
  appInitDetails: AppInitDetails | undefined,
  selectedKID?: string
): HraInfo | null => {
  if (!appInitDetails?.segmentation) return null;
  const { segmentation } = appInitDetails;

  const fromSegmentation = (info?: SegmentationInfo): HraInfo | null =>
    info?.activityId
      ? { campaignId: info.campaignId, activityId: info.activityId }
      : null;

  if (selectedKID) {
    const direct = fromSegmentation(segmentation[selectedKID]);
    if (direct) return direct;
  }

  const activeOption = appInitDetails.eligibilityOptions?.find(
    (o) => o.isActive
  );
  if (activeOption?.clientNumber) {
    const fromActive = fromSegmentation(
      segmentation[activeOption.clientNumber]
    );
    if (fromActive) return fromActive;
  }

  const firstOption = appInitDetails.eligibilityOptions?.[0];
  if (firstOption?.clientNumber) {
    const fromFirst = fromSegmentation(segmentation[firstOption.clientNumber]);
    if (fromFirst) return fromFirst;
  }

  return null;
};

type ProfileShape = {
  customFields?: Array<{ name?: string; value?: string }>;
};

const useSelectedKid = (): string | undefined => {
  const { data } = useGetUserProfile() as { data: ProfileShape | undefined };
  return data?.customFields?.find((f) => f?.name === "primary_kid_selected")
    ?.value;
};

export const useHraInfo = () => {
  const { data, isLoading, error } = useAppInitDetails();
  const selectedKID = useSelectedKid();
  const hraInfo = resolveHraInfo(data, selectedKID);

  return { hraInfo, isLoading, error };
};
